import { deltaE2000, type Lab } from '../color/index.js';
import { leastSquares } from './solve.js';

/**
 * Detecting uneven illumination across the frame.
 *
 * This is a different failure from a noisy sample, and a much larger one in
 * practice. Every correction model in this module is a single global transform
 * applied to the whole image, which assumes the light falling on the scene is
 * the same everywhere in it. When that assumption breaks, the correction is
 * only valid at the exact spot the reference was sampled, and the error at any
 * other spot is however much the illumination differs — a quantity no amount
 * of fitting can recover.
 *
 * It is also invisible to the per-region uniformity check in `capture`: a
 * smooth gradient across the frame leaves every individual sample looking
 * pristine. It has to be measured by comparing samples taken at *different
 * positions* on a surface known to be physically uniform.
 */

/** One reading of a known-uniform surface, with where in the frame it was taken. */
export interface IlluminationProbe {
  /** Position in frame pixels. */
  x: number;
  y: number;
  lab: Lab;
}

export type IlluminationVerdict = 'good' | 'marginal' | 'unusable';

export interface IlluminationAssessment {
  probeCount: number;
  meanLightness: number;
  /** max L* minus min L* across the probes. */
  lightnessSpread: number;
  /** Worst pairwise CIEDE2000 between probes. The headline number. */
  worstDeltaE: number;
  /**
   * Least-squares plane through the probe lightnesses, when at least three
   * non-collinear probes are available. `perThousandPx` is the magnitude of
   * the lightness gradient, which is the useful figure: it says how much L*
   * changes over a 1000-pixel step, independent of frame size.
   */
  gradient: { dLdx: number; dLdy: number; perThousandPx: number } | null;
  verdict: IlluminationVerdict;
  advice: string;
}

/** Below this worst-pair dE00, illumination is not the limiting error. */
export const ILLUMINATION_GOOD_DELTA_E = 2;
/** Above this, a single global correction cannot produce trustworthy readings. */
export const ILLUMINATION_UNUSABLE_DELTA_E = 5;

/**
 * Assess how evenly a scene is lit.
 *
 * `probes` must all be readings of the *same physically uniform surface* —
 * several points on one paint card, say. Any variation between them is
 * measurement error, and since the surface is uniform by construction, that
 * error is dominated by the light.
 */
export function assessIllumination(probes: IlluminationProbe[]): IlluminationAssessment {
  if (probes.length < 2) {
    throw new Error(
      'Assessing illumination needs at least two probes taken at different positions ' +
        'on the same uniform surface.',
    );
  }

  const lightnesses = probes.map((p) => p.lab.L);
  const meanLightness = lightnesses.reduce((a, b) => a + b, 0) / probes.length;
  const lightnessSpread = Math.max(...lightnesses) - Math.min(...lightnesses);

  let worstDeltaE = 0;
  for (let i = 0; i < probes.length; i++) {
    for (let j = i + 1; j < probes.length; j++) {
      worstDeltaE = Math.max(worstDeltaE, deltaE2000((probes[i] as IlluminationProbe).lab, (probes[j] as IlluminationProbe).lab));
    }
  }

  const gradient = fitLightnessPlane(probes);

  const verdict: IlluminationVerdict =
    worstDeltaE <= ILLUMINATION_GOOD_DELTA_E
      ? 'good'
      : worstDeltaE <= ILLUMINATION_UNUSABLE_DELTA_E
        ? 'marginal'
        : 'unusable';

  return {
    probeCount: probes.length,
    meanLightness,
    lightnessSpread,
    worstDeltaE,
    gradient,
    verdict,
    advice: adviceFor(verdict, worstDeltaE, gradient),
  };
}

/** Least-squares fit of L = c0 + c1*x + c2*y. Null when underdetermined. */
function fitLightnessPlane(
  probes: IlluminationProbe[],
): { dLdx: number; dLdy: number; perThousandPx: number } | null {
  if (probes.length < 3) return null;

  // Centre the coordinates so the constant term does not dominate the
  // conditioning of the normal equations.
  const meanX = probes.reduce((a, p) => a + p.x, 0) / probes.length;
  const meanY = probes.reduce((a, p) => a + p.y, 0) / probes.length;

  const rows = probes.map((p) => [1, p.x - meanX, p.y - meanY]);
  const solution = leastSquares(rows, probes.map((p) => p.lab.L));
  if (!solution) return null;

  const dLdx = solution[1] as number;
  const dLdy = solution[2] as number;
  return { dLdx, dLdy, perThousandPx: Math.hypot(dLdx, dLdy) * 1000 };
}

function adviceFor(
  verdict: IlluminationVerdict,
  worstDeltaE: number,
  gradient: { dLdx: number; dLdy: number; perThousandPx: number } | null,
): string {
  if (verdict === 'good') {
    return `Lighting is even across the frame (worst ${worstDeltaE.toFixed(1)} ΔE₀₀ between probes).`;
  }

  const direction = gradient ? ` The gradient runs ${describeDirection(gradient)}.` : '';
  const magnitude = gradient
    ? ` Lightness changes about ${gradient.perThousandPx.toFixed(1)} L* per 1000 px.`
    : '';

  const base =
    `A surface that is physically uniform is reading ${worstDeltaE.toFixed(1)} ΔE₀₀ ` +
    `differently across the frame, so the light is not even.${direction}${magnitude}`;

  const fix =
    ' Move the light source much further away or make it much larger — the falloff is ' +
    'inverse-square, so distance flattens it fast. Overcast daylight or open shade is ' +
    'the easiest way to get this right. Keep the reference and the sample close together ' +
    'in frame until it is fixed.';

  return verdict === 'unusable'
    ? `${base} A single global correction cannot fix this.${fix}`
    : `${base} Readings will be usable but not precise.${fix}`;
}

function describeDirection(gradient: { dLdx: number; dLdy: number }): string {
  const horizontal = gradient.dLdx > 0 ? 'brighter to the right' : 'brighter to the left';
  // Image coordinates put y=0 at the top.
  const vertical = gradient.dLdy > 0 ? 'brighter toward the bottom' : 'brighter toward the top';
  return Math.abs(gradient.dLdx) > Math.abs(gradient.dLdy)
    ? `${horizontal}, and ${vertical}`
    : `${vertical}, and ${horizontal}`;
}
